import type {FooterPreset, SiteContact} from '@olympus-frontend/tenant-config';
import type {ReactNode} from 'react';
// Deep import, not the contact barrel: the footer renders on every page and must
// not drag the contact-page composites (ContactBody, SendMessageForm) along.
import {mailtoHref, whatsappMessageHref} from '../contact/contact-href';
import {AppIcon, FaEmailOutlineIcon, FaLocationOutlineIcon, FaPhoneOutlineIcon, FaWhatsappBrandIcon} from '../icon';

export type ContactMethod = 'hotline' | 'whatsapp' | 'email' | 'address';

type Translate = (key: string) => string;

export interface ContactRowView {
  icon: ReactNode;
  labelKey: string;
  value?: string;
  href?: string;
  inlineLabel?: boolean;
  method: ContactMethod;
}

// Footer contact rows render their glyph at the `sm` token.
const METHOD_ICON: Record<ContactMethod, ReactNode> = {
  address: <AppIcon icon={FaLocationOutlineIcon} sx={{fontSize: '0.875em'}} aria-hidden />,
  hotline: <AppIcon icon={FaPhoneOutlineIcon} sx={{fontSize: '0.875em'}} aria-hidden />,
  whatsapp: <AppIcon icon={FaWhatsappBrandIcon} size="sm" aria-hidden />,
  email: <AppIcon icon={FaEmailOutlineIcon} size="sm" aria-hidden />,
};

const LABEL_KEY: Record<ContactMethod, string> = {
  address: 'sales_office',
  hotline: 'hotline',
  whatsapp: 'whatsapp',
  email: 'email',
};

const INLINE_LABEL_KEY: Record<ContactMethod, string> = {
  address: 'fa-location',
  hotline: 'hotline_footer',
  whatsapp: 'whatsapp_footer',
  email: 'email_footer',
};

function mapsSearchHref(query: string): string {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
}

function row(preset: FooterPreset, method: ContactMethod, value: string, href: string | undefined): ContactRowView {
  if (preset === 'contact-labels') {
    return {icon: METHOD_ICON[method], labelKey: LABEL_KEY[method], href, method};
  }
  return {icon: METHOD_ICON[method], labelKey: INLINE_LABEL_KEY[method], value, href, inlineLabel: method !== 'address', method};
}

export function buildContactRows(contact: SiteContact, preset: FooterPreset, t: Translate): ContactRowView[] {
  const rows: ContactRowView[] = [];
  if (contact.salesCenterAddress) {
    rows.push(row(preset, 'address', contact.salesCenterAddress, contact.salesCenterHref ?? mapsSearchHref(contact.salesCenterAddress)));
  }
  if (contact.hotline) {
    rows.push(row(preset, 'hotline', contact.hotline, `tel:${contact.hotline}`));
  }
  if (contact.whatsapp) {
    rows.push(
      row(preset, 'whatsapp', contact.whatsapp, contact.whatsappHref && whatsappMessageHref(contact.whatsappHref, t('whatsapp_message'))),
    );
  }
  if (contact.email) {
    rows.push(row(preset, 'email', contact.email, mailtoHref(contact.email, t('email_subject'), t('email_body'))));
  }
  return rows;
}
